import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { isVisible } from 'slices/loader/reducer';
import logo from '../assets/images/Logo.png';
import {
  getUploadErrorToastContent,
  getUploadSuccessToastContent,
  getUploadToastType,
} from './uploadError';

type BulkExcelOperationsOptions = {
  exportFile: () => Promise<Blob>;
  getExportFilename: () => string;
  uploadFile: (file: File) => Promise<any>;
  onUploadSuccess?: (data: any) => void | Promise<void>;
};

export const downloadExcelBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;

  document.body.appendChild(link);
  link.click();

  link.remove();
  window.URL.revokeObjectURL(url);
};

export const isSupportedExcelFile = (file: File) =>
  file.name.toLowerCase().endsWith('.xlsx');

export const useBulkExcelOperations = ({
  exportFile,
  getExportFilename,
  uploadFile,
  onUploadSuccess,
}: BulkExcelOperationsOptions) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    try {
      setLoading(true);
      const blob = await exportFile();
      downloadExcelBlob(blob, getExportFilename());
    } catch (err) {
      console.error('Download error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file: File) => {
    if (!file) {
      toast.error('No file selected');
      return;
    }

    if (!isSupportedExcelFile(file)) {
      toast.error('Only Excel files allowed');
      return;
    }

    const toastId = toast.loading(
      <div className="d-flex align-items-center gap-2">
        <img
          src={logo}
          alt="MPM"
          style={{ width: 28, height: 28, objectFit: 'contain' }}
        />
        <span>Uploading file...</span>
      </div>,
    );

    dispatch(isVisible(true));

    try {
      setLoading(true);
      const data = await uploadFile(file);

      toast.update(toastId, {
        render: getUploadSuccessToastContent(data),
        type: getUploadToastType(data),
        isLoading: false,
        autoClose: data?.failedRows?.length || data?.failed > 0 ? 8000 : 3000,
      });

      await onUploadSuccess?.(data);
    } catch (err: any) {
      toast.update(toastId, {
        render: getUploadErrorToastContent(err),
        type: 'error',
        isLoading: false,
        autoClose: 8000,
      });

      console.error('Upload error:', err);
    } finally {
      setLoading(false);
      dispatch(isVisible(false));
    }
  };

  return {
    loading,
    handleExport,
    handleUpload,
  };
};
